// React components entry point - dynamic loading
import { loadReact } from '../utils/react_loader'

// Global root registry to prevent duplicate roots
window.reactRoots = window.reactRoots || {}

// Lazy-loaded dependencies
let QueryClient, QueryClientProvider, standaloneQueryClient

// Initialize React components dynamically
async function initializeReactComponents() {
  try {
    // Queue placeholder so render requests are not lost while React loads
    window._pendingReactRenders = window._pendingReactRenders || []
    window.renderReactComponent = function (componentName, containerId, props = {}) {
      window._pendingReactRenders.push({ componentName, containerId, props })
    }

    const { React, ReactDOM } = await loadReact()

    // Load React Query alongside other components
    const [ConversationIndex, ConversationViewer, DashboardIndex, ErrorBoundary, sentryModule, reactQueryModule] = await Promise.all([
      import('../pages/ConversationIndex'),
      import('./conversations/ConversationViewer'),
      import('../pages/DashboardIndex'),
      import('./ErrorBoundary'),
      import('../utils/sentry'),
      import('@tanstack/react-query'),
    ])

    // Initialize React Query
    QueryClient = reactQueryModule.QueryClient
    QueryClientProvider = reactQueryModule.QueryClientProvider
    standaloneQueryClient = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 1000 * 60 * 5, // 5 minutes
          retry: 1,
        },
      },
    })

    sentryModule.initializeSentry()
    window.Sentry = sentryModule.Sentry
    window.React = React
    window.ReactDOM = { createRoot: ReactDOM.createRoot }

    // Components that need QueryClientProvider wrapper
    const componentsNeedingQueryClient = ['ConversationViewer']

    window.ReactComponents = {
      ConversationIndex: ConversationIndex.default,
      ConversationViewer: ConversationViewer.default,
      DashboardIndex: DashboardIndex.default,
    }

    // Store which components need QueryClientProvider
    window.ReactComponentsNeedingQueryClient = componentsNeedingQueryClient

    // Function to render React components with root management
    window.renderReactComponent = function (componentName, containerId, props = {}) {
      const component = window.ReactComponents[componentName]
      if (!component) {
        console.error(`React component '${componentName}' not found.`)
        return
      }

      const container = document.getElementById(containerId)
      if (!container) {
        console.error(`Container '${containerId}' not found`)
        return
      }

      // Check if we have an existing root for this container
      let root = window.reactRoots[containerId]

      // If root exists but container was replaced by Turbo, create new root
      if (root) {
        try {
          // Test if root is still valid by checking if container is in DOM
          if (!document.body.contains(container) || container._reactRootContainer === undefined) {
            // Container was replaced, unmount old root and create new one
            try {
              root.unmount()
            } catch (e) {
              // Ignore unmount errors
            }
            root = null
            delete window.reactRoots[containerId]
          }
        } catch (e) {
          root = null
          delete window.reactRoots[containerId]
        }
      }

      if (!root) {
        root = ReactDOM.createRoot(container)
        window.reactRoots[containerId] = root
      }

      // Create the component element
      let componentElement = React.createElement(component, props)

      // Wrap with QueryClientProvider if needed (for standalone components)
      if (window.ReactComponentsNeedingQueryClient?.includes(componentName) && QueryClientProvider && standaloneQueryClient) {
        componentElement = React.createElement(
          QueryClientProvider,
          { client: standaloneQueryClient },
          componentElement
        )
      }

      // Always wrap with ErrorBoundary
      root.render(
        React.createElement(ErrorBoundary.default, {}, componentElement)
      )
    }

    // Mark as initialized
    window.reactComponentsInitialized = true

    // Flush any queued renders that happened before React finished loading
    try {
      const pending = window._pendingReactRenders || []
      window._pendingReactRenders = [] // Clear before processing to avoid duplicates
      pending.forEach(({ componentName, containerId, props }) => {
        try {
          window.renderReactComponent(componentName, containerId, props)
        } catch (e) {
          console.error(`Failed to render queued component ${componentName}:`, e)
        }
      })
    } catch (e) {
      console.error('Failed to flush pending renders:', e)
    }
  } catch (error) {
    console.error('Failed to initialize React components:', error)

    // Set error state function
    window.renderReactComponent = function (componentName, containerId, props = {}) {
      console.error(`Cannot render ${componentName}: React initialization failed`)
    }
  }
}

// Lazy bootstrap: placeholder queues requests and kicks off init on first call
window._pendingReactRenders = window._pendingReactRenders || []
window.renderReactComponent = function (componentName, containerId, props = {}) {
  window._pendingReactRenders.push({ componentName, containerId, props })
  if (!window._reactInitStarted) {
    window._reactInitStarted = true
    initializeReactComponents()
  }
}

// Clean up roots when Turbo replaces content
document.addEventListener('turbo:before-render', function () {
  // Clear roots that will be replaced
  Object.keys(window.reactRoots).forEach(containerId => {
    const container = document.getElementById(containerId)
    if (!container || !document.body.contains(container)) {
      try {
        window.reactRoots[containerId]?.unmount()
      } catch (e) {
        // Ignore unmount errors
      }
      delete window.reactRoots[containerId]
    }
  })
})

// Re-initialize components after Turbo loads new content
document.addEventListener('turbo:load', function () {
  if (!window.reactComponentsInitialized && !window._reactInitStarted) {
    window._reactInitStarted = true
    initializeReactComponents()
  }
})

